/* eslint-disable react/no-array-index-key */

import Icon from '@/components/shared/Icons';
import { Button } from '@/components/shared/button/Button';
import type { AIStrings } from '@/utils/filters/options/getFilterOptions';
import { useCallback } from 'react';

type Props = {
  summary: string;
  setQuery: (updatedQuery: string[]) => void;
  strings: AIStrings;
  setTriggerSearch: (b: boolean) => void;
  onOpenEdit: () => void;
  error: { error: boolean; text: string };
  setError: (e: { error: boolean; text: string }) => void;
};

export default function ReadMode({
  summary,
  setTriggerSearch,
  setQuery,
  strings,
  onOpenEdit,
  error,
  setError
}: Props): JSX.Element {
  const updateChat = useCallback(
    (partToBeRemoved: string) => {
      // reset error
      if (error) {
        setError({ error: false, text: '' });
      }
      const cleanSummary = summary.replace(/#/g, '');
      setQuery([cleanSummary, `Remove ${partToBeRemoved.slice(1, partToBeRemoved.length - 1)} from criteria.`]);
      setTriggerSearch(true);
    },
    [error, setError, setQuery, setTriggerSearch, summary]
  );

  const parts = summary?.split(/(#.*?#)/g);

  return (
    <>
      <div className="z-10 mb-3 line-clamp-1 p-3 pb-0 pl-5 pr-1 pt-[18px] text-b-md lg:pt-[14px] lg:text-b-base">
        {parts?.map((part, index) => {
          if (part.startsWith('#') && part.endsWith('#')) {
            return (
              <div
                key={index}
                className="group relative inline-block rounded-md pb-1 text-brand-60 hover:bg-neutral-20 hover:px-2"
              >
                {part.slice(1, part.length - 1)}
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/interactive-supports-focus */}
                <div
                  aria-label={strings.aiRemove}
                  role="button"
                  className="invisible absolute right-0 top-0 -translate-y-1/2 translate-x-1/2 transform cursor-pointer rounded-full px-1 text-[10px] text-[#000000] group-hover:visible group-hover:border-1 group-hover:bg-neutral-20"
                  onClick={e => {
                    e.stopPropagation();
                    updateChat(part);
                  }}
                >
                  <span>x</span>
                </div>
              </div>
            );
          }
          return (
            <span className="mx-0.5" key={index}>
              {part}
            </span>
          );
        })}
      </div>
      <Button
        className="m-2 rounded-full bg-brand-20 p-4"
        icon={<Icon id="edit-pencil" className="h-4 w-4 fill-brand-60" />}
        appearance="ghost"
        onClick={onOpenEdit}
        testID="ai-search-edit-button"
        aria-label={strings.aiEdit}
      />
    </>
  );
}
